import React, { useState, useRef, useEffect, forwardRef } from 'react'

import { splitStyleProps } from '../../utils/splitStyleProps'
import { useMergedRef } from '../../hooks/useMergedRef'
import { Stack } from '../Stack'
import { TextInput, TextInputProps } from '../TextInput'
import { IconButton } from '../IconButton'

export interface IncrementInputProps extends Omit<TextInputProps, 'onChangeValue'> {
  value?: string | number
  defaultValue?: string | number
  onChangeValue?: (value: number) => void
  size?: 'sm' | 'md' | 'lg'
  max?: number
  min?: number
  step?: number
  defaultWidthCh?: number
}

export const IncrementInput = forwardRef(
  (
    {
      value,
      defaultValue,
      onChange,
      onChangeValue,
      onFocus,
      max = Infinity,
      min = -Infinity,
      step = 1,
      size = 'md',
      defaultWidthCh = 5,
      isDisabled,
      ...restProps
    }: IncrementInputProps,
    forwardedRef,
  ) => {
    const initialValue: number = ((value ?? defaultValue) as number) ?? null
    const [_value, setValue] = useState(initialValue)

    const inputRef = useRef(null)
    const mergedRef = useMergedRef(forwardedRef, inputRef)

    const [styleProps, inputProps] = splitStyleProps(restProps)

    const handleDecrement = () => {
      setValue(state => (state - step >= min ? state - step : min))
    }

    const handleIncrement = () => {
      setValue(state => (state + step > max ? state : state + step))
    }

    const handleFocus = event => {
      event.target.select()

      onFocus && onFocus(event)
    }

    const handleChange = event => {
      const { value } = event.target

      if (isValueValid(value)) {
        setValue(value)
      }
    }

    const handleChangeValue = value => {
      if (isValueValid(value)) {
        setValue(value)
      }
    }

    const isValueValid = value => {
      if (value == undefined) return false
      if (isNaN(value)) return false
      if (value > max) return false
      if (value < min) return false
      return true
    }

    useEffect(() => {
      /* Set the width of the input to the default width (in characters)
       * plus the length of the input value. */
      inputRef.current.style.width = `${defaultWidthCh + String(_value).length}ch`
      onChangeValue && onChangeValue(_value)
    }, [_value])

    return (
      <Stack spacing={1} isInline align="center" {...styleProps}>
        <IconButton
          icon="minus"
          bg="transparent"
          color="pink.500"
          isRound
          onClick={handleDecrement}
          size={size}
          title="Decrement"
          aria-label="Decrement"
          isDisabled={isDisabled}
        />

        <TextInput
          type="tel"
          autoComplete="none"
          textAlign="center"
          value={_value}
          onChange={handleChange}
          onChangeValue={handleChangeValue}
          onFocus={handleFocus}
          size={size}
          ref={mergedRef}
          width={`${defaultWidthCh}ch`}
          isDisabled={isDisabled}
          {...inputProps}
        />

        <IconButton
          icon="add"
          bg="transparent"
          color="pink.500"
          isRound
          onClick={handleIncrement}
          size={size}
          title="Increment"
          aria-label="Increment"
          isDisabled={isDisabled}
        />
      </Stack>
    )
  },
)

export default IncrementInput
